"use client";
import { Input, useDisclosure, useToast } from "@chakra-ui/react";
import { useContext, useEffect, useState } from "react";
import moment from "moment";
import { GetMedicalHistory } from "@/actions/templates/MedicalHistory/GetMedicalHistory";
import HistorySection from "@/components/dashboard/history/HistorySection";
import HistoryRecordsTable from "@/components/dashboard/HistoryRecordsTable";
import {
  EVENT_LIBRARY,
  HISTORY_TEMPLATES,
  PhoneNumberMask,
  REQUIRED_FIELD,
} from "@/lib/constants";
import { DeleteMedicalHistory } from "@/actions/templates/MedicalHistory/DeleteMedicalHistory";
import {
  MedicalHistoryPayload,
  PostMedicalHistory,
} from "@/actions/templates/MedicalHistory/PostMedicalHistory";
import { Controller, useForm } from "react-hook-form";
import FormRow from "@/components/dashboard/application/FormRow";
import GoogleAddressInput from "@/components/dashboard/GoogleAddressInput";
import HistoryModal from "@/components/dashboard/history/HistoryModal";
import { PutMedicalHistory } from "@/actions/templates/MedicalHistory/PutMedicalHistory";
import { AuthContext } from "@/providers/auth";
import PhoneNumberInput from "@/components/forms/PhoneNumberInput";
import ZipCodeInput from "@/components/forms/ZipCodeInput";
import useAnalytics from "@/lib/hooks/useAnalytics";
import { format } from "@react-input/mask";
import handleServerAction from "@/lib/hooks/handleServerAction";
import { useIsTextMasked } from "../children/useIsDataMasked";
import { InputWrapper } from "@/components/forms/JailTextInputWrapper";

const category = "Doctors";
const defaultValues = HISTORY_TEMPLATES[category].defaultValues;

const DoctorsHistoryPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [records, setRecords] = useState<any[]>([]);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { getUserDetails } = useContext(AuthContext);
  const toast = useToast();
  const track = useAnalytics();

  const {
    handleSubmit,
    register,
    reset,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<MedicalHistoryPayload>({
    defaultValues: defaultValues,
  });

  const onEdit = (id: number) => {
    const editRecord = records.find((record) => record.id === id);
    if (editRecord) {
      reset({
        Id: editRecord?.id,
        PhysicianName: editRecord?.physicianName,
        TreatmentStartDate: moment(editRecord?.treatmentStartDate).format(
          "YYYY-MM-DD"
        ),
        LastVisitDate: moment(editRecord?.lastVisitDate).format("YYYY-MM-DD"),
        NextScheduledVisitDate: moment(
          editRecord?.nextScheduledVisitDate
        ).format("YYYY-MM-DD"),
        PhysicianAddress: editRecord?.physicianAddress,
        PhysicianPhoneNumber: format(
          editRecord?.physicianPhoneNumber || "",
          PhoneNumberMask
        ),
        MedicationReason: editRecord?.medicationReason,
        PhysicianType: editRecord?.physicianType,
        HospitalName: editRecord?.hospitalName,
        Zip: editRecord?.zip,
        FaxNumber: format(editRecord?.faxNumber || "", PhoneNumberMask),
      });
      onOpen();
    }
  };

  const refreshState = async () => {
    onClose();
    await getRecords();
    await getUserDetails();
    reset(defaultValues);
  };
  const onAdd = async (payload: MedicalHistoryPayload) => {
    try {
      await handleServerAction(PostMedicalHistory, payload);
      await refreshState();

      toast({
        status: "success",
        title: "Record Added",
        description: "Record has been successfully added.",
      });

      track(EVENT_LIBRARY.ADDED_HISTORY, {
        category,
      });
    } catch (err) {
      toast({
        status: "error",
        title: "Error Occured",
        description: err.message,
      });
    }
  };

  const onUpdate = async (payload: MedicalHistoryPayload) => {
    try {
      await handleServerAction(PutMedicalHistory, payload);
      await refreshState();

      toast({
        status: "success",
        title: "Record Updated",
        description: "Record has been successfully updated.",
      });

      track(EVENT_LIBRARY.UPDATED_HISTORY, {
        category,
      });
    } catch (err) {
      toast({
        status: "error",
        title: "Error Occured",
        description: err.message,
      });
    }
  };

  const getRecords = async () => {
    setIsLoading(true);
    const data = await handleServerAction(GetMedicalHistory);
    setRecords(data);
    setIsLoading(false);
  };

  useEffect(() => {
    getRecords();
  }, []);

  const onClick = () => {
    reset(defaultValues);
    onOpen();
  };

  const Id = watch("Id");
  const isTextMasked = useIsTextMasked() && Id;

  return (
    <HistorySection category={category} isLoading={isLoading} onClick={onClick}>
      <HistoryRecordsTable
        category={category}
        records={records}
        cells={records.map((record) => [
          record.hospitalName,
          moment(record.treatmentStartDate).format("M/D/YY"),
          record.medicationReason,
        ])}
        onEdit={onEdit}
        deleteAction={DeleteMedicalHistory}
        getRecords={getRecords}
      />
      <HistoryModal
        category={category}
        mode={Id ? "Update" : "Add"}
        isOpen={isOpen}
        onClose={onClose}
        onAdd={handleSubmit(onAdd)}
        onUpdate={handleSubmit(onUpdate)}
        isSubmitting={isSubmitting}
      >
        <FormRow
          label="Physician Name"
          size="sm"
          fieldError={errors.PhysicianName}
        >
          <InputWrapper
            name="PhysicianName"
            control={control}
            rules={{ required: REQUIRED_FIELD }}
            placeholder="Dr. Seuss"
            maskInputField={isTextMasked}
          />
        </FormRow>
        <FormRow
          label="Name of Hospital/Facility"
          size="sm"
          fieldError={errors.HospitalName}
        >
          <InputWrapper
            name="HospitalName"
            control={control}
            rules={{ required: REQUIRED_FIELD }}
            placeholder="Mindset General"
            maskInputField={isTextMasked}
          />
        </FormRow>
        <FormRow
          label="First Visit Date"
          size="sm"
          fieldError={errors.TreatmentStartDate}
        >
          <Input
            type="date"
            textTransform="uppercase"
            {...register("TreatmentStartDate", { required: REQUIRED_FIELD })}
          />
        </FormRow>
        <FormRow
          label="Last Visit Date"
          size="sm"
          fieldError={errors.LastVisitDate}
        >
          <Input
            type="date"
            textTransform="uppercase"
            {...register("LastVisitDate", { required: REQUIRED_FIELD })}
          />
        </FormRow>
        <FormRow
          label="Next Scheduled Visit Date"
          size="sm"
          fieldError={errors.NextScheduledVisitDate}
        >
          <Input
            type="date"
            textTransform="uppercase"
            {...register("NextScheduledVisitDate", {
              required: REQUIRED_FIELD,
            })}
          />
        </FormRow>
        <FormRow
          label="Phone Number"
          size="sm"
          fieldError={errors.PhysicianPhoneNumber}
        >
          <PhoneNumberInput
            register={register}
            fieldName="PhysicianPhoneNumber"
          />
        </FormRow>
        <FormRow
          label="Address of Hospital/Facility"
          size="sm"
          fieldError={errors.PhysicianAddress}
        >
          <Controller
            name="PhysicianAddress"
            control={control}
            rules={{ required: REQUIRED_FIELD }}
            render={({ field }) => (
              <GoogleAddressInput
                value={field.value}
                onChange={field.onChange}
                setValue={setValue}
                prefillFields={["Zip"]}
              />
            )}
          />
        </FormRow>
        <FormRow
          label="Address 2 (Optional)"
          size="sm"
          fieldError={errors.AddressTwo}
        >
          <InputWrapper
            name="AddressTwo"
            control={control}
            placeholder="i.e. Apt, Ste, Fl, Unit"
            maskInputField={isTextMasked}
          />
        </FormRow>
        <FormRow label="Zip Code" size="sm" fieldError={errors.Zip}>
          <ZipCodeInput register={register} />
        </FormRow>
        <FormRow
          label="Reason for Visit"
          size="sm"
          fieldError={errors.MedicationReason}
        >
          <InputWrapper
            name="MedicationReason"
            control={control}
            rules={{ required: REQUIRED_FIELD }}
            placeholder=""
            maskInputField={isTextMasked}
          />
        </FormRow>

        {/* <FormRow label="Fax Number" size="sm" fieldError={errors.FaxNumber}>
          <PhoneNumberInput register={register} fieldName="FaxNumber" />
        </FormRow>
        <FormRow
          label="Physician Type"
          description="Select all that apply"
          alignItems="flex-start"
          size="sm"
          fieldError={errors.PhysicianType}
        >
          <Controller
            name="PhysicianType"
            control={control}
            rules={{ required: REQUIRED_FIELD }}
            render={({ field: { value, onChange } }) => {
              const selectedValues = value ? value.split(",") : [];

              const handleCheckboxChange = (checkboxValue: string) => {
                const updatedValues = selectedValues.includes(checkboxValue)
                  ? selectedValues.filter((val) => val !== checkboxValue)
                  : [...selectedValues, checkboxValue];
                onChange(updatedValues.join(","));
              };

              return (
                <Stack direction="column" gap="12px">
                  <Checkbox
                    value="1"
                    isChecked={selectedValues.includes("1")}
                    onChange={() => handleCheckboxChange("1")}
                  >
                    Medical Physician
                  </Checkbox>
                  <Checkbox
                    value="2"
                    isChecked={selectedValues.includes("2")}
                    onChange={() => handleCheckboxChange("2")}
                  >
                    Psychiatrist
                  </Checkbox>
                  <Checkbox
                    value="3"
                    isChecked={selectedValues.includes("3")}
                    onChange={() => handleCheckboxChange("3")}
                  >
                    Psychologist / Therapist
                  </Checkbox>
                </Stack>
              );
            }}
          />
        </FormRow> */}
      </HistoryModal>
    </HistorySection>
  );
};

export default DoctorsHistoryPage;
